import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon.js';
import type { AppUpdate } from './app-update.js';
import './app-update.css';

const labels = {
  ru: {
    title: 'Доступна новая версия', hint: 'Обновите приложение, когда закончите работу.',
    update: 'Обновить', updating: 'Обновляем…', cancel: 'Позже',
    confirm: 'Обновить приложение?',
    warning: 'Открытые рабочие вкладки и формы в этом окне закроются. Несохранённые изменения будут потеряны. Сохраните нужные данные перед обновлением.',
    offline: 'Сохранённые черновики и очередь офлайн-отправки останутся на устройстве.',
    error: 'Не удалось обновить. Формы сохранены открытыми. Попробуйте ещё раз.',
  },
  uz: {
    title: 'Yangi versiya mavjud', hint: 'Ishni tugatgach, ilovani yangilang.',
    update: 'Yangilash', updating: 'Yangilanmoqda…', cancel: 'Keyinroq',
    confirm: 'Ilova yangilansinmi?',
    warning: 'Ushbu oynadagi ochiq ish varaqlari va shakllar yopiladi. Saqlanmagan o‘zgarishlar yo‘qoladi. Yangilashdan oldin kerakli ma’lumotlarni saqlang.',
    offline: 'Saqlangan qoralamalar va oflayn yuborish navbati qurilmada qoladi.',
    error: 'Yangilab bo‘lmadi. Shakllar ochiq qoldi. Qayta urinib ko‘ring.',
  },
};

export function AppUpdateNotice({ update, language, field = false, beforeReload }: {
  update: AppUpdate; language: string; field?: boolean; beforeReload?: () => void;
}) {
  const state = useSyncExternalStore(update.subscribe, update.getSnapshot);
  const [confirming, setConfirming] = useState(false);
  const confirmingRef = useRef(false);
  confirmingRef.current = confirming;
  const dialog = useRef<HTMLDivElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const notice = useRef<HTMLElement>(null);
  const text = labels[language.startsWith('uz') ? 'uz' : 'ru'];

  useEffect(() => {
    if (!field || !state.available || !notice.current) return;
    const measure = () => {
      const height = notice.current?.getBoundingClientRect().bottom ?? 0;
      document.documentElement.style.setProperty('--jb-app-update-space', `${Math.ceil(height + 12)}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(notice.current);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--jb-app-update-space');
    };
  }, [field, state.available]);

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (!confirmingRef.current) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!update.getSnapshot().applying) setConfirming(false);
      }
      if (event.key === 'Tab') {
        const buttons = dialog.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
        if (!buttons?.length) { event.preventDefault(); return; }
        const first = buttons[0]!;
        const last = buttons[buttons.length - 1]!;
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    // Install at startup, before an editor's Escape handlers can be mounted.
    window.addEventListener('keydown', keydown, true);
    return () => window.removeEventListener('keydown', keydown, true);
  }, [update]);

  useEffect(() => {
    if (!confirming) return;
    const previous = document.activeElement as HTMLElement | null;
    cancel.current?.focus();
    const background = [...document.body.children].filter((element): element is HTMLElement =>
      element instanceof HTMLElement && !element.classList.contains('jb-app-update-overlay'));
    const inert = background.map((element) => element.inert);
    background.forEach((element) => { element.inert = true; });
    return () => {
      background.forEach((element, index) => { element.inert = inert[index] ?? false; });
      if (previous?.isConnected) previous.focus();
    };
  }, [confirming]);

  if (!state.available) return null;
  return createPortal(<>
    <aside ref={notice} className={`jb-app-update${field ? ' jb-app-update--field' : ''}`} role="status" aria-live="polite">
      <Icon name="refresh" size={20} />
      <div className="jb-app-update__copy">
        <strong>{text.title}</strong>
        <span>{state.failed ? text.error : text.hint}</span>
      </div>
      <button type="button" onClick={() => setConfirming(true)} disabled={state.applying}>
        {state.applying ? text.updating : text.update}
      </button>
    </aside>
    {confirming && <div className="jb-app-update-overlay" onClick={() => !state.applying && setConfirming(false)}>
      <div className="jb-app-update-dialog" ref={dialog} role="dialog" aria-modal="true"
        aria-labelledby="jb-update-title" aria-describedby="jb-update-warning" onClick={(event) => event.stopPropagation()}>
        <Icon name="refresh" size={24} />
        <h2 id="jb-update-title">{text.confirm}</h2>
        <p id="jb-update-warning">{text.warning}</p>
        {field && <p>{text.offline}</p>}
        {state.failed && <p role="alert">{text.error}</p>}
        <div className="jb-app-update-dialog__actions">
          <button type="button" ref={cancel} disabled={state.applying} onClick={() => setConfirming(false)}>{text.cancel}</button>
          <button type="button" disabled={state.applying} onClick={() => void update.accept(beforeReload)}>
            {state.applying ? text.updating : text.update}
          </button>
        </div>
      </div>
    </div>}
  </>, document.body);
}
